import {
    Alert,
    Stack,
    Text,
} from '@mantine/core';
import {modals} from '@mantine/modals';
import {AiOutlineInfoCircle} from 'react-icons/ai';

import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';

import {usePlatformControls} from '../hooks/usePlatformControls';
import {ControlCard} from './ControlCard';
import {
    ControlState,
    PlatformControl,
} from '../types';

export const ControlsList = () => {

    const {
        controls,
        pending,
        readError,
        toggleControl,
    } = usePlatformControls();

    const {allowedActions} = useVerifiedPermissions();
    const canOperate = allowedActions.includes(rbacActions.OperatePlatformControl);

    const confirmToggle = (control: PlatformControl, next: ControlState) => {

        const turningOff = next === ControlState.OFF;

        modals.openConfirmModal({
                                    title: `${turningOff ? 'Turn off' : 'Turn on'} ${control.name.toLowerCase()}`,
                                    children: (
                                        <Stack gap={'xs'}>
                                            <Text size={'sm'}>
                                                This takes effect across the whole platform, not just this
                                                console.
                                            </Text>
                                            {typeof control.flipLatencySeconds === 'number' && (
                                                <Text size={'sm'} c={'dimmed'}>
                                                    It can take up to {control.flipLatencySeconds} seconds
                                                    to apply.
                                                </Text>
                                            )}
                                        </Stack>
                                    ),
                                    labels: {
                                        confirm: turningOff ? 'Turn off' : 'Turn on',
                                        cancel: 'Cancel',
                                    },
                                    confirmProps: {color: turningOff ? 'red' : 'teal'},
                                    onConfirm: () => {
                                        void toggleControl(control.id, next)
                                            .then((result) => {
                                                if (result.applied) {
                                                    notifySuccess(
                                                        `${control.name} is now ${turningOff ? 'off' : 'on'}.`,
                                                    );
                                                    return;
                                                }
                                                // Never let the UI imply a flip that did not happen.
                                                notifyError(result.reason);
                                            });
                                    },
                                });
    };

    return (
        <Stack gap={'lg'} m={'sm'}>

            <Alert
                color={'blue'}
                variant={'light'}
                icon={<AiOutlineInfoCircle/>}
                title={'What these are'}
            >
                Operational controls change how the running platform behaves, without a deploy.
                They are not feature flags — each one has a normal resting position it stays in
                almost all the time, and is moved deliberately in response to a situation.
                Controls that are not wired up yet are listed too, so the work they still need
                stays visible.
            </Alert>

            {readError && (
                <Alert color={'red'} variant={'light'} title={'Could not read the current state'}>
                    One or more controls could not be read, so their position is shown as unknown.
                    This does not change how the platform is behaving — it only means this page
                    cannot confirm it right now.
                </Alert>
            )}

            {controls.map((control) => (
                <ControlCard
                    key={control.id}
                    control={control}
                    busy={pending.includes(control.id)}
                    canOperate={canOperate}
                    onToggle={(next) => confirmToggle(control, next)}
                />
            ))}

        </Stack>
    );
};
